import React, { useEffect, useRef, useState } from "react";
import { MoreVertical, UserRound, LogOut, ShoppingBag, User } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import {
  CUSTOMER_CHANGE_EVENT,
  getCachedCustomer,
  logoutCustomer,
} from "@/lib/customerAuth";

// Strict 3-dot menu — ONLY exposes Profile, My Orders, Logout. No other
// navigation items are permitted in this dropdown.
export const AccountChip = ({ onLogout, onMyOrders, onProfile }) => {
  const [customer, setCustomer] = useState(() => getCachedCustomer());
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    const onChange = () => setCustomer(getCachedCustomer());
    window.addEventListener(CUSTOMER_CHANGE_EVENT, onChange);
    window.addEventListener("storage", onChange);
    return () => {
      window.removeEventListener(CUSTOMER_CHANGE_EVENT, onChange);
      window.removeEventListener("storage", onChange);
    };
  }, []);

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (!wrapRef.current?.contains(e.target)) setOpen(false);
    };
    window.addEventListener("mousedown", onClick);
    return () => window.removeEventListener("mousedown", onClick);
  }, [open]);

  if (!customer) return null;

  const handleLogout = () => {
    logoutCustomer();
    setOpen(false);
    onLogout?.();
  };

  return (
    <div className="relative" ref={wrapRef}>
      <button
        onClick={() => setOpen((s) => !s)}
        data-testid="header-account-chip"
        aria-label="account menu"
        className="w-10 h-10 rounded-full bg-[#E0B53C]/15 hover:bg-[#E0B53C]/25 text-[#E0B53C] inline-flex items-center justify-center transition-all"
      >
        <MoreVertical size={18} strokeWidth={2} />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.15 }}
            className="absolute right-0 top-full mt-2 w-56 rounded-2xl bg-white border border-black/5 shadow-[0_18px_48px_rgba(0,0,0,0.18)] p-2 z-50"
            data-testid="header-account-menu"
          >
            <button
              onClick={() => {
                setOpen(false);
                onProfile?.();
              }}
              data-testid="account-menu-profile"
              className="w-full inline-flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium text-[#0B0C10] hover:bg-neutral-100 transition-colors"
            >
              <User size={14} strokeWidth={1.8} />
              Profile
            </button>
            <button
              onClick={() => {
                setOpen(false);
                onMyOrders?.();
              }}
              data-testid="account-menu-my-orders"
              className="w-full inline-flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium text-[#0B0C10] hover:bg-neutral-100 transition-colors"
            >
              <ShoppingBag size={14} strokeWidth={1.8} />
              My Orders
            </button>
            <button
              onClick={handleLogout}
              data-testid="account-menu-logout"
              className="w-full inline-flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium text-red-600 hover:bg-red-50 transition-colors"
            >
              <LogOut size={14} strokeWidth={1.8} />
              Logout
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Header trigger button — visible only when NOT signed in.
export const SignInButton = ({ onClick }) => (
  <button
    onClick={onClick}
    data-testid="header-signin-btn"
    className="inline-flex items-center gap-1.5 rounded-full bg-[#E0B53C] hover:bg-[#f0c44a] text-[#022C22] px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-semibold transition-all"
  >
    <UserRound size={14} strokeWidth={2} />
    Sign in
  </button>
);
